/**
 * HL7 v2 timestamp parser. Covers the TS, DT, and DTM datatypes which all share the same
 * format (DT is just a TS truncated to date precision, DTM is a TS with no `DegreeOfPrecision`
 * suffix). Format per HL7 v2.5 §2.A.84.1:
 *
 *   YYYY[MM[DD[HH[MM[SS[.S[S[S[S]]]]]]]]][+/-ZZZZ]
 *
 * The precision varies per message — a date-only field (`19720131`) and a full datetime
 * (`20260507093015-0400`) both pass through this parser, and the returned `precision` lets
 * the renderer format them appropriately ("Jan 31, 1972" vs "May 7, 2026, 9:30 AM EDT").
 *
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/TS — Time Stamp
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/DTM — Date/Time
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes/DT — Date
 */

const HL7_TIMESTAMP_PATTERN =
  /^(?<year>\d{4})(?<month>\d{2})?(?<day>\d{2})?(?<hour>\d{2})?(?<minute>\d{2})?(?<second>\d{2})?(?<fraction>\.\d+)?(?<offset>[+-]\d{4})?$/;

export type Hl7TimestampPrecision = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';

export interface ParsedHl7Timestamp {
  /**
   * JavaScript Date carrying the source's wall-clock components placed at UTC. We deliberately
   * do NOT normalize zoned inputs to the absolute UTC instant — instead, the wall-clock is
   * constructed via `Date.UTC(...)` regardless of the source offset so downstream display via
   * `timeZone: 'UTC'` reads back exactly what the source message carried (e.g. `093000-0400`
   * displays as "9:30 AM", not "1:30 PM"). Consumers who need the absolute instant can recover
   * it from `date` and `offsetMinutes` together.
   */
  date: Date;
  /** Lowest unit that was actually present in the source. Drives display formatting. */
  precision: Hl7TimestampPrecision;
  /** True when the source carried an explicit `+/-ZZZZ` offset; false when it was zone-less. */
  hasExplicitTimezone: boolean;
  /**
   * Source offset in minutes east of UTC (e.g. `-240` for `-0400`, `300` for `+0500`); undefined
   * for zone-less inputs. Preserved so downstream renderers can annotate displays with the
   * source zone and so consumers needing the absolute UTC instant can compute it.
   */
  offsetMinutes?: number;
}

const parseOffsetMinutes = (offsetSpec: string): number => {
  // offsetSpec is `+HHMM` or `-HHMM`. Convert to minutes east of UTC.
  const sign = offsetSpec.startsWith('-') ? -1 : 1;
  const hours = Number.parseInt(offsetSpec.slice(1, 3), 10);
  const minutes = Number.parseInt(offsetSpec.slice(3, 5), 10);
  return sign * (hours * 60 + minutes);
};

export const parseHl7Timestamp = (raw: string): ParsedHl7Timestamp | null => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const match = HL7_TIMESTAMP_PATTERN.exec(trimmed);
  if (!match?.groups) {
    return null;
  }

  const { year, month, day, hour, minute, second, fraction, offset } = match.groups;

  // Determine precision based on which fields were present. Hour is allowed without minute
  // for non-spec inputs we still want to preserve faithfully ("2026050709" → 9 AM May 7);
  // promoting to 'minute' / 'second' kicks in only when those parts are also present.
  let precision: Hl7TimestampPrecision = 'year';
  if (month) {
    precision = 'month';
  }
  if (day) {
    precision = 'day';
  }
  if (hour) {
    precision = 'hour';
  }
  if (hour && minute) {
    precision = 'minute';
  }
  if (second) {
    precision = 'second';
  }

  // Build the Date. We always treat the source wall-clock as UTC — for zone-less inputs this
  // avoids the implicit "interpret in the JS engine's local zone" behavior that would make
  // date-only fields render as the day before/after for users in different timezones; for
  // zoned inputs (`+/-ZZZZ`) we preserve the source-local wall-clock rather than normalizing
  // to the absolute UTC instant, so downstream display in UTC reads back what the source
  // carried. The offset itself is preserved on `offsetMinutes` for callers that need it.
  const yearNum = Number.parseInt(year, 10);
  const monthNum = month ? Number.parseInt(month, 10) - 1 : 0;
  const dayNum = day ? Number.parseInt(day, 10) : 1;
  const hourNum = hour ? Number.parseInt(hour, 10) : 0;
  const minuteNum = minute ? Number.parseInt(minute, 10) : 0;
  const secondNum = second ? Number.parseInt(second, 10) : 0;
  const millisecondNum = fraction ? Math.round(Number.parseFloat(`0${fraction}`) * 1000) : 0;

  const utcMs = Date.UTC(yearNum, monthNum, dayNum, hourNum, minuteNum, secondNum, millisecondNum);

  return {
    date: new Date(utcMs),
    precision,
    hasExplicitTimezone: Boolean(offset),
    offsetMinutes: offset ? parseOffsetMinutes(offset) : undefined,
  };
};

const FORMATTER_OPTIONS_BY_PRECISION: Record<Hl7TimestampPrecision, Intl.DateTimeFormatOptions> = {
  year: { year: 'numeric', timeZone: 'UTC' },
  month: { year: 'numeric', month: 'short', timeZone: 'UTC' },
  day: { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' },
  hour: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    timeZone: 'UTC',
  },
  minute: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  },
  second: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
  },
};

/**
 * Format a parsed timestamp for display. Uses UTC consistently to avoid the wandering-day-by-zone
 * problem; we trust the source message's offset (if any) as ground truth.
 */
export const formatHl7Timestamp = (parsed: ParsedHl7Timestamp): string => {
  const options = FORMATTER_OPTIONS_BY_PRECISION[parsed.precision];
  return new Intl.DateTimeFormat(undefined, options).format(parsed.date);
};

const pad2 = (value: number): string => value.toString().padStart(2, '0');

/**
 * Produce a value suitable for an HTML `<time dateTime>` attribute. The attribute accepts
 * several ISO formats; pick the one that matches the source's precision so screen readers and
 * browser parsers don't infer a more specific time than the message actually carried.
 *
 *   precision === 'year'    → "1972"
 *   precision === 'month'   → "1972-01"
 *   precision === 'day'     → "1972-01-31"
 *   precision === 'hour'    → "2026-05-07T09:00:00.000Z" (full ISO; spec accepts this)
 *   precision === 'minute'  → "2026-05-07T09:30:00.000Z"
 *   precision === 'second'  → "2026-05-07T09:30:15.000Z"
 */
export const formatHl7TimestampIsoForDateTimeAttribute = (parsed: ParsedHl7Timestamp): string => {
  const date = parsed.date;
  switch (parsed.precision) {
    case 'year':
      return `${date.getUTCFullYear()}`;
    case 'month':
      return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}`;
    case 'day':
      return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
    case 'hour':
    case 'minute':
    case 'second':
      return date.toISOString();
    default:
      // Defensive: the switch above is exhaustive over `Hl7TimestampPrecision` today; falling
      // back to full ISO keeps us correct if a precision is added later.
      return date.toISOString();
  }
};
